<template>
  <div>
    <div v-if="data">
      <div v-if="content.showFilterBar" class="pb-4">
        <div class="b-b-2 b-brand-1 py-4">
          <div class="flex items-end gap-x-4">
            <h2 class="subtitle-2">
              {{ $t.articleCategories }}
            </h2>
            <vf-carousel-elevate class-container="gap-x-8" :show-arrows="false">
              <base-button class="subtitle-5" :class="{ 'fw-bold underlined': !activeFilters.length }" @click="clearAll">
                {{ $t.articleAllCategories }}
              </base-button>
              <base-button
                v-for="filter in filtersToShow"
                :key="filter.id"
                class="subtitle-5"
                :class="{ underlined: activeFilters.includes(filter.id) }"
                @click="toggleFilter(filter.id)"
              >
                {{ filter.label }}
              </base-button>
            </vf-carousel-elevate>
          </div>
        </div>
      </div>
      <div>
        <h2 v-if="content.title" class="pb-6 pt-8 text-center title-1">
          <span v-if="showTitleHtml" v-html="content.titleHtml" />
          <span v-else>{{ content.title }}</span>
        </h2>
      </div>
      <base-grid :class="paddingClasses" :cols="content.gridSize" gap="xs">
        <div
          v-for="(article, i) in articlesToShow"
          :key="article.id"
          :class="{ [offsetClasses]: (i + 2) % 3 === 0 }"
        >
          <vf-article-card-v2
            v-bind="article"
            :image="{
              src: getMediaUrl(article.picture?.url),
              alt: article.picture.alt,
            }"
            :tags="[article.eyebrow]"
            :to="article.url"
          />
        </div>
      </base-grid>
      <div v-if="content.loadMore && articlesToShow.length < data?.numFound" class="py-6 text-center">
        <vf-button :loading="pending" @click="loadMore">
          {{ $t.articleLoadMore }}
        </vf-button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ArticleGridContent } from '#types/components/cms/article-grid'
import type { SearchData } from '#types/product'

const { content } = defineProps<{ content: ArticleGridContent }>()
const { getMediaUrl, getSearch } = useCms()
const { showTitleHtml } = useFeatureFlags()

const elevatedBreakpoints = Object.entries(content.gridSize)
  .filter(([_, value]) => value === 3) // magic number, offset has to be applied only to 3cards grid
  .flatMap(([key]) => key)

const paddingClasses = elevatedBreakpoints.length && Object.values({
  sm: elevatedBreakpoints.includes('sm') && '<md:pb-10',
  md: elevatedBreakpoints.includes('md') && '~md:pb-10',
  lg: elevatedBreakpoints.includes('lg') && 'lg:pb-10',
}).filter(Boolean).join(' ')

const offsetClasses = elevatedBreakpoints.length && Object.values({
  sm: elevatedBreakpoints.includes('sm') && '<md:transform-translate-y-10',
  md: elevatedBreakpoints.includes('md') && '~md:transform-translate-y-10',
  lg: elevatedBreakpoints.includes('lg') && 'lg:transform-translate-y-10',
}).filter(Boolean).join(' ')

const DEFAULT_ROWS_TO_LOAD = 3

const rowsToLoad = ref(content.rowsToLoad * content.gridSize.sm! || DEFAULT_ROWS_TO_LOAD)
const offset = ref(0)

const parseQuery = (query: string): Record<string, string[]> => {
  const urlParams = new URLSearchParams(query)
  return Array.from(urlParams.entries()).reduce((acc, [key, value]) => {
    if (acc[key])
      acc[key] = [...acc[key], value]
    else
      acc[key] = [value]
    return acc
  }, {})
}

const queryParsed = parseQuery(content.initialQuery)
const queryWithoutFiltersParsed = parseQuery(content.initialQueryNotFiltered)
const activeFilters = useRouteQuery<string[]>(
  `component${content.id}[tagId]`,
  queryParsed.tagId || [],
  { transform: (param) => [param].flat() }
)

const query = computed(() => ({
  ...queryParsed,
  offset: offset.value,
  limit: rowsToLoad.value,
  tagId: activeFilters.value
}))

const pending = ref(true)
const data = ref<SearchData>()

const fetchArticles = async () => {
  pending.value = true
  try {
    const response = await getSearch('article', query.value) as SearchData
    data.value = {
      ...response,
      result: [
        ...offset.value !== 0 && data.value?.result ? data.value.result : [],
        ...response?.result || []
      ]
    }
  }
  catch (error: any) {
    log.error(`Error fetching articles:`, error)
  }
  finally {
    pending.value = false
  }
}

fetchArticles()

const articlesToShow = computed(() => data.value?.result || [])

const filtersToShow = computed(() => data.value?.facets)

const loadMore = () => {
  offset.value += rowsToLoad.value
  fetchArticles()
}

const clearAll = () => {
  activeFilters.value = queryWithoutFiltersParsed.tagId || []
  fetchArticles()
}

const toggleFilter = (filter: string) => {
  if (activeFilters.value.includes(filter))
    activeFilters.value = activeFilters.value.filter((f) => f !== filter)
  else
    activeFilters.value = [...activeFilters.value, filter]
  offset.value = 0
  fetchArticles()
}
</script>
